import { useState, useRef, useEffect } from 'react';
import type { TaskRow } from '../api/types';
import { formatDate, isOverdue } from '../lib/time';
import { useSwipe } from '../hooks/useSwipe';
import { haptic } from '../hooks/useHaptics';

interface Props {
  task: TaskRow;
  selected: boolean;
  onClick: () => void;
  onToggleComplete: () => void;
  onToggleStarred?: () => void;
  onLongPress?: (rect: DOMRect) => void;
}

// 优先级: 整行淡 tint + 细左边线, 一档饱和度 (柔和但能看清)
const IMPORTANCE_TINT: Record<number, string> = {
  0: '',
  1: 'border-l-orange-400 bg-orange-50/70 dark:bg-orange-900/20',
  2: 'border-l-red-400 bg-red-50/70 dark:bg-red-900/20',
};
const IMPORTANCE_DOT: Record<number, string> = {
  0: '',
  1: 'bg-orange-500',
  2: 'bg-red-500',
};
const IMPORTANCE_CHIP: Record<number, string> = {
  0: '',
  1: 'bg-orange-200 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  2: 'bg-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};
const IMPORTANCE_LABEL: Record<number, string> = {
  0: '',
  1: '中',
  2: '高',
};

export function TaskItem({
  task,
  selected,
  onClick,
  onToggleComplete,
  onToggleStarred,
  onLongPress,
}: Props) {
  const [swipeState, setSwipeState] = useState<'left' | 'right' | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);

  const { offset, swiping, handlers, reset } = useSwipe({
    threshold: 70,
    maxOffset: 90,
    onSwipeLeft: () => {
      // 左滑 = 标记完成
      if (!task.completed) {
        haptic('success');
        onToggleComplete();
      } else {
        haptic('light');
      }
      reset();
    },
    onSwipeRight: () => {
      // 右滑 = 标记/取消重要
      haptic('selection');
      onToggleStarred?.();
      reset();
    },
  });

  // 视觉反馈: 当偏移超过 30 时显示色块
  useEffect(() => {
    if (offset < -30) setSwipeState('left');
    else if (offset > 30) setSwipeState('right');
    else setSwipeState(null);
  }, [offset]);

  // 长按 0.5s 触发
  const onTouchStart = (e: React.TouchEvent) => {
    handlers.onTouchStart(e);
    longPressFired.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      haptic('heavy');
      const el = e.currentTarget as HTMLElement;
      onLongPress?.(el.getBoundingClientRect());
    }, 500);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    handlers.onTouchMove(e);
    // 一旦明显移动, 取消长按
    if (Math.abs(offset) > 5 && longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    handlers.onTouchEnd(e);
  };

  // 屏蔽 click 当长按/swipe 触发了
  const handleClick = (e: React.MouseEvent) => {
    if (longPressFired.current || swiping) {
      e.preventDefault();
      e.stopPropagation();
      longPressFired.current = false;
      return;
    }
    onClick();
  };

  return (
    <div className="relative overflow-hidden rounded">
      {/* swipe 背景 (左滑 = 完成绿色 / 右滑 = 重要黄色) */}
      {swipeState === 'left' && (
        <div className="absolute inset-0 flex items-center justify-end bg-green-500 pr-4 text-white">
          <span className="text-sm font-medium">✓ 完成</span>
        </div>
      )}
      {swipeState === 'right' && (
        <div className="absolute inset-0 flex items-center justify-start bg-yellow-400 pl-4 text-white">
          <span className="text-sm font-medium">{task.starred ? '☆ 取消重要' : '★ 标重要'}</span>
        </div>
      )}

      <div
        onClick={handleClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: swiping ? `translateX(${offset}px)` : undefined,
          transition: swiping ? 'none' : 'transform 0.2s ease-out',
        }}
        className={`fx-add group relative flex cursor-pointer items-stretch rounded border-l-2 text-sm transition ${
          selected
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
            : `${IMPORTANCE_TINT[task.importance] || 'border-transparent'} hover:bg-gray-50 dark:hover:bg-gray-800`
        }`}
      >
        <div className="flex flex-1 items-start gap-2 px-2 py-2">
          {/* 完成圆圈 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              haptic(task.completed ? 'light' : 'success');
              onToggleComplete();
            }}
            className={`fx-tap fx-complete mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition ${
              task.completed
                ? 'border-blue-500 bg-blue-500 text-white'
                : 'border-gray-300 group-hover:border-blue-400 dark:border-gray-600'
            }`}
            aria-label={task.completed ? '取消完成' : '标记完成'}
          >
            {!!task.completed && (
              <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>

          {/* 标题 + 元信息 */}
          <div className="min-w-0 flex-1">
            <div
              className={`truncate ${task.completed ? 'line-through text-gray-400' : ''}`}
            >
              {task.title}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
              {task.importance > 0 && (
                <span
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${IMPORTANCE_CHIP[task.importance]}`}
                  title={`优先级${task.importance === 2 ? '高' : '中'}`}
                >
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${IMPORTANCE_DOT[task.importance]}`} />
                  {IMPORTANCE_LABEL[task.importance]}
                </span>
              )}
              {task.due_date && (
                <span
                  className={
                    isOverdue(task.due_date) && !task.completed
                      ? 'text-red-500'
                      : 'text-gray-400'
                  }
                >
                  📅 {formatDate(task.due_date)}
                </span>
              )}
              {task.reminder_at && !task.completed && (
                <span className="text-gray-400" title="有提醒">🔔</span>
              )}
              {task.note && <span className="text-gray-400" title="有备注">📝</span>}
              {task.starred && <span className="text-yellow-500">★</span>}
            </div>
          </div>

          {/* 重要星 (可点) */}
          {onToggleStarred && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                haptic('selection');
                onToggleStarred();
              }}
              className={`fx-tap fx-star shrink-0 self-center px-1 text-base ${
                task.starred
                  ? 'text-yellow-500'
                  : 'text-gray-300 group-hover:text-gray-400'
              }`}
              title={task.starred ? '取消重要' : '标记重要'}
              aria-label="重要"
            >
              {task.starred ? '★' : '☆'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
